/**
 * FamilleResponsablesPanel — items 12.1 et 12.2.
 *
 * **12.1 — les deux parents.** Le modèle est multi-tuteurs depuis toujours ;
 * ce qui manquait, c'était de le RENDRE VISIBLE. Chaque responsable affiche
 * si son compte VPC est rattaché — et sinon, on dit quoi faire (il s'inscrit
 * avec cette adresse, le rattachement se fait tout seul).
 *
 * **12.2 — la suspension.** Manager/admin uniquement, pièce justificative
 * obligatoire. Le motif et la pièce ne quittent JAMAIS cet écran : côté
 * famille, l'API ne les renvoie même pas.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import type { ScolaireResponsable } from '@roronoa-collectivites/shared-types';
import { Button, Badge, Alert } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { AlertCircle, UserPlus, ShieldOff, ShieldCheck, MailWarning } from 'lucide-react';

const CHAMP = 'h-9 w-full rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink focus:border-accent focus:outline-none';

interface Props {
  familleId: string;
  responsables: ScolaireResponsable[];
  /** true pour manager/admin : seuls eux voient les motifs et peuvent suspendre. */
  estEncadrement: boolean;
  onChange: () => void;
}

export function FamilleResponsablesPanel({ familleId, responsables, estEncadrement, onChange }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [ajout, setAjout] = useState(false);
  const [f, setF] = useState({ prenom: '', nom: '', email: '', telephone: '', lien: 'parent' as const });
  const [blocage, setBlocage] = useState<ScolaireResponsable | null>(null);
  const [bMotif, setBMotif] = useState('');
  const [bDebut, setBDebut] = useState('');
  const [bFin, setBFin] = useState('');
  const [bPiece, setBPiece] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = useCallback(() => {
    setF({ prenom: '', nom: '', email: '', telephone: '', lien: 'parent' });
    setAjout(false);
  }, []);

  useEffect(() => { setError(null); setMsg(null); }, [familleId]);

  async function agir(fn: () => Promise<unknown>, ok: string) {
    setBusy(true); setError(null); setMsg(null);
    try {
      await fn();
      setMsg(ok);
      onChange();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Action impossible');
    } finally { setBusy(false); }
  }

  return (
    <div className="rounded-r-lg border border-border bg-surface/30 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium text-ink">Responsables légaux</h4>
        {!ajout && (
          <Button variant="secondary" size="sm" onClick={() => setAjout(true)}>
            <UserPlus className="h-4 w-4" aria-hidden="true" /> Ajouter un parent
          </Button>
        )}
      </div>

      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {responsables.length === 0 && !ajout && (
        <p className="text-sm text-ink-muted">Aucun responsable enregistré.</p>
      )}

      <ul className="space-y-1.5">
        {responsables.map((r) => (
          <li key={r.id} className="rounded-r-md border border-border/60 px-2.5 py-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-medium text-ink">
                {[r.prenom, r.nom].filter(Boolean).join(' ') || 'Sans nom'}
              </span>
              <Badge variant="neutral" size="sm">{r.lien}</Badge>
              {r.principal && <Badge variant="info" size="sm">Principal</Badge>}
              {r.compteRattache
                ? <Badge variant="success" size="sm">Compte rattaché</Badge>
                : <Badge variant="warning" size="sm">Pas de compte</Badge>}
              {r.bloque && <Badge variant="urgent" size="sm">Déclarations suspendues</Badge>}
            </div>
            {r.email && <p className="text-xs text-ink-muted">{r.email}</p>}

            {/* 12.1 — dire quoi faire, pas seulement constater */}
            {!r.compteRattache && r.email && (
              <p className="mt-1 flex items-start gap-1 text-xs text-ink-muted">
                <MailWarning className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Ce parent n'a pas encore de compte. Qu'il s'inscrive sur le portail
                avec <strong className="text-ink">{r.email}</strong> : le rattachement
                à cette famille se fera automatiquement, et il pourra déclarer les
                repas et les présences de son côté.
              </p>
            )}

            {/* 12.2 — réservé à l'encadrement */}
            {estEncadrement && (
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {r.bloque ? (
                  <>
                    {r.blocageMotif && (
                      <span className="text-xs text-ink-muted">
                        Motif : {r.blocageMotif}
                        {r.blocagePieceNom ? ` · pièce : ${r.blocagePieceNom}` : ''}
                        {r.blocageDebut ? ` · depuis le ${r.blocageDebut}` : ''}
                      </span>
                    )}
                    <Button variant="ghost" size="sm" disabled={busy}
                      onClick={() => void agir(
                        () => api.scolaire.leverBlocageResponsable(r.id, 'Levée par la mairie'),
                        'Déclarations rétablies — le parent est prévenu.')}>
                      <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Rétablir
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" size="sm"
                    onClick={() => { setBlocage(r); setBMotif(''); setBDebut(''); setBFin(''); setBPiece(null); }}>
                    <ShieldOff className="h-4 w-4" aria-hidden="true" /> Suspendre les déclarations
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* ── Ajout d'un responsable ─────────────────────────────────────────── */}
      {ajout && (
        <div className="mt-2 space-y-2 border-t border-border/60 pt-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <input value={f.prenom} onChange={(e) => setF({ ...f, prenom: e.target.value })}
              placeholder="Prénom" aria-label="Prénom du responsable" className={CHAMP} />
            <input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })}
              placeholder="Nom" aria-label="Nom du responsable" className={CHAMP} />
            <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
              placeholder="Adresse e-mail" aria-label="E-mail du responsable" type="email" className={CHAMP} />
            <input value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })}
              placeholder="Téléphone" aria-label="Téléphone du responsable" className={CHAMP} />
          </div>
          <p className="text-xs text-ink-muted">
            Si ce parent a déjà un compte avec cette adresse, il est rattaché
            immédiatement. Sinon, le rattachement se fera à son inscription.
          </p>
          <div className="flex gap-1.5">
            <Button variant="primary" size="sm" disabled={busy || (!f.nom.trim() && !f.email.trim())}
              onClick={() => void agir(async () => {
                await api.scolaire.addResponsable({ familleId, ...f });
                reset();
              }, 'Responsable ajouté.')}>
              Enregistrer
            </Button>
            <Button variant="ghost" size="sm" onClick={reset}>Annuler</Button>
          </div>
        </div>
      )}

      {/* ── Suspension : motif + pièce obligatoires ────────────────────────── */}
      <Dialog open={!!blocage} onClose={() => setBlocage(null)}
        title="Suspendre les déclarations d'un responsable"
        footer={
          <>
            <Button variant="ghost" onClick={() => setBlocage(null)}>Annuler</Button>
            <Button variant="primary" disabled={busy || bMotif.trim().length < 3 || !bPiece}
              onClick={() => void agir(async () => {
                await api.scolaire.bloquerResponsable(blocage!.id, {
                  motif: bMotif.trim(),
                  dateDebut: bDebut || undefined,
                  dateFin: bFin || undefined,
                }, bPiece!);
                setBlocage(null);
              }, 'Déclarations suspendues — le parent est prévenu.')}>
              Suspendre
            </Button>
          </>
        }>
        <div className="space-y-3">
          <p className="text-sm text-ink-muted">
            Ce parent gardera l'accès en <strong className="text-ink">consultation</strong>
            {' '}(repas, présences, factures) mais ne pourra plus <strong className="text-ink">déclarer</strong>.
            Il recevra un message neutre l'invitant à se rapprocher de la mairie —
            <strong className="text-ink"> le motif ne lui est jamais communiqué</strong>.
          </p>
          <label className="block text-sm text-ink">
            Motif (interne)
            <textarea value={bMotif} onChange={(e) => setBMotif(e.target.value)} rows={3}
              placeholder="Ex : ordonnance du juge aux affaires familiales du 12/06/2026"
              className="mt-1 w-full rounded-r-md border border-border bg-surface/60 px-2.5 py-1.5 text-sm text-ink focus:border-accent focus:outline-none" />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block text-sm text-ink">
              À partir du (facultatif)
              <input type="date" value={bDebut} onChange={(e) => setBDebut(e.target.value)} className={`mt-1 ${CHAMP}`} />
            </label>
            <label className="block text-sm text-ink">
              Jusqu'au (facultatif)
              <input type="date" value={bFin} onChange={(e) => setBFin(e.target.value)} className={`mt-1 ${CHAMP}`} />
            </label>
          </div>
          <label className="block text-sm text-ink">
            Pièce justificative <span className="text-danger">(obligatoire)</span>
            <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => setBPiece(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-sm text-ink-muted" />
          </label>
          <p className="text-xs text-ink-muted">
            La décision doit pouvoir être justifiée : décision de justice, dépôt
            de plainte, attestation. La pièce reste visible du seul encadrement.
          </p>
        </div>
      </Dialog>
    </div>
  );
}
